import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { ReadOnlyCodeViewer } from 'components/core/editor/ReadOnlyCodeViewer'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Check, Copy } from 'lucide-react'
import { FC, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { SuccessSnippet } from './workloadSourcePresets'

type CodeSnippetProps = {
    snippet: SuccessSnippet
}

/**
 * Renders a code snippet with a filename header, syntax-highlighted code via
 * ReadOnlyCodeViewer (Monaco), and a copy-to-clipboard button.
 */
export const CodeSnippet: FC<CodeSnippetProps> = ({ snippet }) => {
    const intl = useIntl()
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback((): void => {
        navigator.clipboard.writeText(snippet.code)
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
    }, [snippet.code])

    return <Box
        sx={{
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden',
        }}
    >
        {/* Header bar with filename and copy button */}
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: 'grey.100',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                minHeight: 32,
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {snippet.filename ?? snippet.language}
            </Typography>
            <Tooltip
                title={copied
                    ? <FormattedMessage id="wf_snippet_copied" defaultMessage="Copied" />
                    : <FormattedMessage id="wf_snippet_copy" defaultMessage="Copy to clipboard" />
                }
            >
                <C1IconButton
                    size="small"
                    onClick={handleCopy}
                    aria-label={intl.formatMessage({ id: 'wf_snippet_copy_aria', defaultMessage: 'Copy' })}
                    sx={{
                        height: 24,
                        width: 24,
                        opacity: 0.6,
                        '&:hover': { opacity: 1 },
                    }}
                >
                    <C1Icon icon={copied ? Check : Copy} sx={{ fontSize: 14 }} />
                </C1IconButton>
            </Tooltip>
        </Stack>

        {/* Code content via Monaco read-only editor */}
        <ReadOnlyCodeViewer
            value={snippet.code}
            language={snippet.language === 'hcl' ? 'plaintext' : snippet.language}
            maxHeight="300px"
        />
    </Box>
}
